import { StageDef } from '../automationMachineInterfaces'
import {
  closeModal,
  getSettingOption,
  setSettingOption,
  RedditNotificationModalOption,
} from './modalUtils'
import { REDDIT_AUTOMATION_STAGE_STATUSES } from './redditAutomationStageStatuses'
import * as redditUrls from './redditUrls'
import { getSwitchValue, toggleSwitch } from './switchUtils'
import { RedditSettingKey } from '../../recommendations/recommendationSettingsKeys'
import * as enums from '../../utils/enums'
import {
  defaultCantUndoFn,
  defaultIsDisabledFn,
  defaultIsDisabledReasonFn,
  getCurrentValueFnFromKey,
  getIsFulfilledFnFromKey,
} from '../recommendationCardDisplayUtils'

type NotificationsSetting = {
  key: RedditSettingKey
  label: string
  section: string
  dataTestId: string
}

const SETTINGS_SECTION = 'settings-notifications-section'

const settings: NotificationsSetting[] = [
  // MESSAGES
  {
    key: RedditSettingKey.NOTIFICATION_PRIVATE_MESSAGES,
    label: 'Private messages',
    section: SETTINGS_SECTION,
    dataTestId: 'PRIVATE_MESSAGE',
  },
  {
    key: RedditSettingKey.NOTIFICATION_CHAT_MESSAGES,
    label: 'Chat messages',
    section: SETTINGS_SECTION,
    dataTestId: 'CHAT_MESSAGE',
  },
  {
    key: RedditSettingKey.NOTIFICATION_CHAT_REQUESTS,
    label: 'Chat requests',
    section: SETTINGS_SECTION,
    dataTestId: 'CHAT_REQUEST',
  },
  // ACTIVITY
  {
    key: RedditSettingKey.NOTIFICATION_MENTIONS,
    label: 'Mentions of u/username',
    section: SETTINGS_SECTION,
    dataTestId: 'USERNAME_MENTION',
  },
  {
    key: RedditSettingKey.NOTIFICATION_COMMENTS,
    label: 'Comments on your posts',
    section: SETTINGS_SECTION,
    dataTestId: 'POST_REPLY',
  },
  {
    key: RedditSettingKey.NOTIFICATION_UPVOTES,
    label: 'Upvotes on your posts',
    section: SETTINGS_SECTION,
    dataTestId: 'UPVOTE_POST',
  },
  {
    key: RedditSettingKey.NOTIFICATION_COMMENT_UPVOTES,
    label: 'Upvotes on your comments',
    section: SETTINGS_SECTION,
    dataTestId: 'UPVOTE_COMMENT',
  },
  {
    key: RedditSettingKey.NOTIFICATION_COMMENT_REPLIES,
    label: 'Replies to your comments',
    section: SETTINGS_SECTION,
    dataTestId: 'COMMENT_REPLY',
  },
  {
    key: RedditSettingKey.NOTIFICATION_FOLLOWERS,
    label: 'New followers',
    section: SETTINGS_SECTION,
    dataTestId: 'USER_NEW_FOLLOWER',
  },
  {
    key: RedditSettingKey.NOTIFICATION_AWARDS,
    label: 'Awards you receive',
    section: SETTINGS_SECTION,
    dataTestId: 'AWARD_RECEIVED',
  },
  {
    key: RedditSettingKey.NOTIFICATION_POST_FOLLOW,
    label: 'Posts you follow',
    section: SETTINGS_SECTION,
    dataTestId: 'POST_FOLLOW',
  },
  {
    key: RedditSettingKey.NOTIFICATION_COMMENT_FOLLOW,
    label: 'Comments you follow',
    section: SETTINGS_SECTION,
    dataTestId: 'COMMENT_FOLLOW',
  },
  {
    key: RedditSettingKey.NOTIFICATION_ACHIEVEMENT,
    label: 'Achievement updates',
    section: SETTINGS_SECTION,
    dataTestId: 'GAMIFICATION_ACHIEVEMENT_UNLOCKED',
  },
  {
    key: RedditSettingKey.NOTIFICATION_REMINDER,
    label: 'Streak reminders',
    section: SETTINGS_SECTION,
    dataTestId: 'GAMIFICATION_REMINDER',
  },
  {
    key: RedditSettingKey.NOTIFICATION_POST_INSIGHTS,
    label: 'Insights on your posts',
    section: SETTINGS_SECTION,
    dataTestId: 'POST_INSIGHTS_RECAP',
  },
  // RECOMMENDATIONS
  {
    key: RedditSettingKey.NOTIFICATION_TRENDING_POSTS,
    label: 'Trending posts',
    section: SETTINGS_SECTION,
    dataTestId: 'LIFECYCLE_POST_SUGGESTIONS',
  },
  {
    key: RedditSettingKey.NOTIFICATION_REREDDIT,
    label: 'ReReddit',
    section: SETTINGS_SECTION,
    dataTestId: 'REREDDIT',
  },
  {
    key: RedditSettingKey.NOTIFICATION_FEATURED_CONTENT,
    label: 'Featured content',
    section: SETTINGS_SECTION,
    dataTestId: 'CURATED_ONE_OFF',
  },
  // UPDATES
  {
    key: RedditSettingKey.NOTIFICATION_ANNOUNCEMENTS,
    label: 'Reddit announcements',
    section: SETTINGS_SECTION,
    dataTestId: 'ONE_OFF',
  },
  {
    key: RedditSettingKey.NOTIFICATION_CAKE_DAY,
    label: 'Cake day',
    section: SETTINGS_SECTION,
    dataTestId: 'CAKE_DAY',
  },
  // MODERATION
  {
    key: RedditSettingKey.NOTIFICATION_MOD_NOTIFICATIONS,
    label: 'Mod notifications',
    section: SETTINGS_SECTION,
    dataTestId: 'MODERATED_SR_ENGAGEMENT',
  },
]

function getCorrectValue(): boolean {
  return false
}

function getCorrectModalValue(
  key: RedditSettingKey,
): RedditNotificationModalOption {
  switch (key) {
    case RedditSettingKey.NOTIFICATION_PRIVATE_MESSAGES:
    case RedditSettingKey.NOTIFICATION_CHAT_MESSAGES:
    case RedditSettingKey.NOTIFICATION_CHAT_REQUESTS:
    case RedditSettingKey.NOTIFICATION_COMMENTS:
    case RedditSettingKey.NOTIFICATION_COMMENT_REPLIES:
    case RedditSettingKey.NOTIFICATION_FOLLOWERS:
    case RedditSettingKey.NOTIFICATION_POST_FOLLOW:
    case RedditSettingKey.NOTIFICATION_COMMENT_FOLLOW:
    case RedditSettingKey.NOTIFICATION_ANNOUNCEMENTS:
    case RedditSettingKey.NOTIFICATION_MOD_NOTIFICATIONS:
      return RedditNotificationModalOption.INBOX
    case RedditSettingKey.NOTIFICATION_MENTIONS:
    case RedditSettingKey.NOTIFICATION_UPVOTES:
    case RedditSettingKey.NOTIFICATION_COMMENT_UPVOTES:
    case RedditSettingKey.NOTIFICATION_AWARDS:
    case RedditSettingKey.NOTIFICATION_ACHIEVEMENT:
    case RedditSettingKey.NOTIFICATION_REMINDER:
    case RedditSettingKey.NOTIFICATION_POST_INSIGHTS:
    case RedditSettingKey.NOTIFICATION_TRENDING_POSTS:
    case RedditSettingKey.NOTIFICATION_REREDDIT:
    case RedditSettingKey.NOTIFICATION_FEATURED_CONTENT:
    case RedditSettingKey.NOTIFICATION_CAKE_DAY:
      return RedditNotificationModalOption.ALL_OFF
    default:
      throw new Error(`Invalid RedditSettingKey: ${key}`)
  }
}

async function getEitherCurrentValue(
  s: NotificationsSetting,
  options: {
    leaveModalOpen: boolean
    removeModalManually: boolean
  } = {
    leaveModalOpen: false,
    removeModalManually: false,
  },
): Promise<{ currentValue: string | boolean; newSettingsFound: boolean }> {
  return Promise.any([
    getSettingOption(s.section, s.dataTestId, options).then((result) => ({
      currentValue: result,
      newSettingsFound: true,
    })),
    getSwitchValue(s.section, s.label, s.label).then((result) => ({
      currentValue: result,
      newSettingsFound: false,
    })),
  ])
}

const getCurrentValueFn = (key: string) =>
  getCurrentValueFnFromKey(key, (value) => {
    if (value === true) {
      return 'On'
    }
    if (value === false) {
      return 'Off'
    }
    return value
  })

const getIsFulfilledFn = (key: string) =>
  getIsFulfilledFnFromKey(
    key,
    (value) =>
      value === false ||
      value === getCorrectModalValue(key as RedditSettingKey),
  )

const gatherStageDefs: StageDef[] = [
  // Force reload so we have a clean page to work with
  {
    type: enums.StageType.ACTION,
    key: `GATHER_REDDIT_NOTIFICATIONS_FORCE_RELOAD`,
    status: REDDIT_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: redditUrls.homeUrl,
    action: async () => {},
  },
  ...settings.map((s) => ({
    type: enums.StageType.ACTION,
    key: `GATHER_REDDIT_SETTING_${s.key}`,
    status: REDDIT_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: redditUrls.notificationsSettingsUrl,
    action: async (_machineContext, userContext) => {
      const { currentValue } = await getEitherCurrentValue(s, {
        leaveModalOpen: false,
        removeModalManually: true,
      })
      await userContext.updateStoredInitialValues(
        { [s.key]: currentValue },
        enums.RedditRecommendationKey.REDDIT_NOTIFICATIONS,
      )
    },
  })),
]

const getCantUndoFn = () => defaultCantUndoFn

const getIsDisabledFn = () => defaultIsDisabledFn

const getIsDisabledReasonFn = () => defaultIsDisabledReasonFn

const updateStageDefs: StageDef[] = [
  // Force reload so we have a clean page to work with
  {
    type: enums.StageType.ACTION,
    key: `UPDATE_REDDIT_NOTIFICATIONS_FORCE_RELOAD`,
    status: REDDIT_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: redditUrls.homeUrl,
    action: async () => {},
  },
  ...settings.map((s) => ({
    type: enums.StageType.ACTION,
    key: `UPDATE_SETTING_${s.key}`,
    status: REDDIT_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: redditUrls.notificationsSettingsUrl,
    action: async (_machineContext, userContext) => {
      const { isDry, settingsSelected } = userContext
      if (!settingsSelected || !settingsSelected[s.key]) {
        return
      }

      const { currentValue, newSettingsFound } = await getEitherCurrentValue(
        s,
        { leaveModalOpen: true, removeModalManually: false },
      )

      const correctValue = newSettingsFound
        ? getCorrectModalValue(s.key)
        : getCorrectValue()

      if (!isDry && currentValue !== correctValue) {
        if (newSettingsFound) {
          await setSettingOption(
            s.section,
            s.dataTestId,
            correctValue as RedditNotificationModalOption,
            true, // Modal is open
          )
        } else {
          await toggleSwitch(s.section, s.label, s.label)
        }
      }
      if (isDry || (newSettingsFound && currentValue === correctValue)) {
        await closeModal()
      }
    },
  })),
]

const undoStageDefs: StageDef[] = [
  // Force reload so we have a clean page to work with
  {
    type: enums.StageType.ACTION,
    key: `UNDO_REDDIT_NOTIFICATIONS_FORCE_RELOAD`,
    status: REDDIT_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: redditUrls.homeUrl,
    action: async () => {},
  },
  ...settings.map((s) => ({
    type: enums.StageType.ACTION,
    key: `UNDO_SETTING_${s.key}`,
    status: REDDIT_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: redditUrls.notificationsSettingsUrl,
    action: async (_machineContext, userContext) => {
      const { initialValues, isDry } = userContext
      if (!initialValues || !initialValues[s.key]) {
        return
      }

      const { currentValue, newSettingsFound } = await getEitherCurrentValue(
        s,
        { leaveModalOpen: true, removeModalManually: false },
      )

      if (!isDry && currentValue !== initialValues[s.key]) {
        if (newSettingsFound) {
          await setSettingOption(
            s.section,
            s.dataTestId,
            initialValues[s.key] as RedditNotificationModalOption,
            true, // Modal is open
          )
        } else {
          await toggleSwitch(s.section, s.label, s.label)
        }
      }
      if (
        isDry ||
        (newSettingsFound && currentValue === initialValues[s.key])
      ) {
        await closeModal()
      }
    },
  })),
]

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
